import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { OccEndpointsService } from '@spartacus/core';
import { Observable } from 'rxjs';
import { pluck } from 'rxjs/operators';
import { CART_MODIFICATION_NORMALIZER } from '../../../features/cart/base/connector/converters';
import { CartModification, OrderEntry, Unit } from '../../model';
import { Occ } from '../../occ';
import { ConverterService } from '../../util';

@Injectable({
  providedIn: 'root',
})
export class ShoppingListEntryConnector {
  constructor(
    private httpClient: HttpClient,
    private occEndpointsService: OccEndpointsService,
    private converterService: ConverterService
  ) {}

  add(
    userId: string,
    code: string,
    articleNumber: string,
    quantity: number,
    unit: Unit,
    note?: string,
    displayNumber?: string
  ): Observable<CartModification> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded',
    });

    const url = this.occEndpointsService.buildUrl('shoppingList', {
      urlParams: { userId: userId, shoppingListCode: code },
      queryParams: { articleNumber, quantity, unit: unit.code, note, displayNumber },
    });
    return this.httpClient
      .patch<Occ.CartModification>(url, {}, { headers })
      .pipe(this.converterService.pipeable(CART_MODIFICATION_NORMALIZER));
  }

  remove(userId: string, code: string, entry: OrderEntry): Observable<CartModification> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded',
    });
    const url = this.occEndpointsService.buildUrl('shoppingListEntry', {
      urlParams: {
        userId: userId,
        shoppingListCode: code,
        entryNumber: `${entry.entryNumber}`,
      },
    });
    return this.httpClient
      .delete<Occ.CartModification>(url, { headers })
      .pipe(this.converterService.pipeable(CART_MODIFICATION_NORMALIZER));
  }

  removeMultiple(userId: string, code: string, entries: OrderEntry[]): Observable<CartModification[]> {
    const url = this.occEndpointsService.buildUrl('shoppingListEntriesMultiple', {
      urlParams: {
        userId,
        shoppingListCode: code,
      },
    });

    return this.httpClient
      .request<Occ.CartModificationList>('delete', url, { body: entries.map((entry) => `${entry.entryNumber}`) })
      .pipe(pluck('cartModifications'), this.converterService.pipeableMany(CART_MODIFICATION_NORMALIZER));
  }

  update(
    userId: string,
    code: string,
    entry: OrderEntry,
    quantity: number,
    unit: Unit,
    note?: string,
    statisticsCode?: string
  ): Observable<CartModification | {}> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded',
    });
    if (note || note === '') {
      const url = this.occEndpointsService.buildUrl('shoppingListEntryNote', {
        urlParams: { userId: userId, shoppingListCode: code, entryNumber: `${entry?.entryNumber}` },
        queryParams: { newTemplateNote: note },
      });
      return this.httpClient.put(url, {}, { headers });
    } else if (statisticsCode || statisticsCode === '') {
      const url = this.occEndpointsService.buildUrl('shoppingListEntryStatisticsCode', {
        urlParams: { userId: userId, shoppingListCode: code, entryNumber: `${entry?.entryNumber}` },
        queryParams: { newTemplateStatisticsCode: statisticsCode },
      });
      return this.httpClient.put(url, {}, { headers });
    } else {
      const url = this.occEndpointsService.buildUrl('shoppingListEntry', {
        urlParams: { userId: userId, shoppingListCode: code, entryNumber: `${entry?.entryNumber}` },
        queryParams: { quantity, unit: unit.code },
      });
      return this.httpClient
        .patch<Occ.CartModification>(url, {}, { headers })
        .pipe(this.converterService.pipeable(CART_MODIFICATION_NORMALIZER));
    }
  }

  replace(
    userId: string,
    code: string,
    entry: OrderEntry,
    articleNumber: string,
    quantity: number,
    unit: Unit
  ): Observable<CartModification> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded',
    });
    const url = this.occEndpointsService.buildUrl('shoppingListEntry', {
      urlParams: {
        userId: userId,
        shoppingListCode: code,
        entryNumber: `${entry.entryNumber}`,
      },
      queryParams: {
        articleNumber,
        quantity,
        unit: unit.code,
      },
    });
    return this.httpClient
      .put<Occ.CartModification>(url, {}, { headers })
      .pipe(this.converterService.pipeable(CART_MODIFICATION_NORMALIZER));
  }
}
